import { AssetClassPage } from "~/modules/asset_classes/types";
import {
    PRESETS,
    PRESET_MAX_ASSETS,
} from "~/modules/assets/components/AssetPicker";
import { Filter } from "~/modules/filters/types";
import { AssetColumns } from "~/modules/assets/types";
import { SortDirections } from "~/modules/filters/types";
import { FilterOperators } from "~/modules/filters/types";
import { Asset } from "~/modules/assets/types";
import { NetFlowsScale } from "~/modules/visualization/components/NetFlowsChart/component";
import type { DateRange } from "react-day-picker";
import { format, isValid, parse } from "date-fns";
import * as React from "react";

// // // //

export type NetFlowField = "chg_7d_amt" | "chg_30d_amt" | "chg_90d_amt";

const SELECTION_URL_PARAM = "asset_ids";
const PRESET_URL_PARAM = "preset";
const MIN_NET_FLOW_URL_PARAM = "min_net_flow";
const SCALE_URL_PARAM = "scale";
const START_DATE_URL_PARAM = "start_date";
const END_DATE_URL_PARAM = "end_date";

// Serialization format for date-range URL params (date-only, no time).
const DATE_URL_FORMAT = "yyyy-MM-dd";

// Default minimum absolute net flow — filters out negligible flows by default.
const DEFAULT_MIN_NET_FLOW = 1;

// Default chart axis scale.
const DEFAULT_SCALE: NetFlowsScale = "linear";

// Chart sizing: per-item row height shrinks as the chart gets busier (taller
// rows when there are few assets, tighter rows when there are many), with the
// total height clamped so the chart is never too small or too tall.
const CHART_MIN_HEIGHT = 240;
const CHART_MAX_HEIGHT = 6000;
const CHART_MAX_PER_ITEM = 52;
const CHART_MIN_PER_ITEM = 22;
const CHART_FEW_ITEMS = 5;
const CHART_MANY_ITEMS = 40;

export function getChartHeight(itemCount: number): number {
    if (itemCount <= 0) {
        return CHART_MIN_HEIGHT;
    }
    // Interpolate per-item height from max (few items) to min (many items).
    const t = Math.min(
        1,
        Math.max(
            0,
            (itemCount - CHART_FEW_ITEMS) /
                (CHART_MANY_ITEMS - CHART_FEW_ITEMS),
        ),
    );
    const perItem =
        CHART_MAX_PER_ITEM - t * (CHART_MAX_PER_ITEM - CHART_MIN_PER_ITEM);

    return Math.round(
        Math.min(
            CHART_MAX_HEIGHT,
            Math.max(CHART_MIN_HEIGHT, perItem * itemCount),
        ),
    );
}

export function parseAssetIDsFromURL(): number[] {
    if (typeof window === "undefined") {
        return [];
    }
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(SELECTION_URL_PARAM);
    if (!raw) {
        return [];
    }
    return raw
        .split(",")
        .map((part) => Number(part.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
}

export function writeAssetIDsToURL(assetIDs: number[]) {
    if (typeof window === "undefined") {
        return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete(PRESET_URL_PARAM);
    if (assetIDs.length > 0) {
        url.searchParams.set(SELECTION_URL_PARAM, assetIDs.join(","));
    } else {
        url.searchParams.delete(SELECTION_URL_PARAM);
    }
    window.history.replaceState(null, "", url.toString());
}

export function parsePresetFromURL(): AssetClassPage | null {
    if (typeof window === "undefined") {
        return null;
    }
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(PRESET_URL_PARAM);
    const match = PRESETS.find((preset) => preset.key === raw);
    return match ? match.key : null;
}

export function writePresetToURL(presetKey: string) {
    if (typeof window === "undefined") {
        return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete(SELECTION_URL_PARAM);
    url.searchParams.set(PRESET_URL_PARAM, presetKey);
    window.history.replaceState(null, "", url.toString());
}

export function getPresetAssetsQuery(filter?: Filter) {
    return {
        query: {
            sort: {
                field: AssetColumns.bridgedTokenValueDollar,
                direction: SortDirections.desc,
            },
            pagination: {
                page: 1,
                perPage: PRESET_MAX_ASSETS,
            },
            ...(filter ? { filter } : {}),
        },
    };
}

export function parseMinNetValueFromURL(): number | null {
    if (typeof window === "undefined") {
        return null;
    }
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(MIN_NET_FLOW_URL_PARAM);
    if (raw === null) {
        return null;
    }
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : null;
}

export function writeMinNetValueToURL(minNetValue: number) {
    if (typeof window === "undefined") {
        return;
    }
    const url = new URL(window.location.href);
    if (minNetValue !== DEFAULT_MIN_NET_FLOW) {
        url.searchParams.set(MIN_NET_FLOW_URL_PARAM, String(minNetValue));
    } else {
        url.searchParams.delete(MIN_NET_FLOW_URL_PARAM);
    }
    window.history.replaceState(null, "", url.toString());
}

export function parseScaleFromURL(): NetFlowsScale | null {
    if (typeof window === "undefined") {
        return null;
    }
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(SCALE_URL_PARAM);
    if (raw === "linear" || raw === "symlog") {
        return raw;
    }
    return null;
}

export function writeScaleToURL(scale: NetFlowsScale) {
    if (typeof window === "undefined") {
        return;
    }
    const url = new URL(window.location.href);
    if (scale !== DEFAULT_SCALE) {
        url.searchParams.set(SCALE_URL_PARAM, scale);
    } else {
        url.searchParams.delete(SCALE_URL_PARAM);
    }
    window.history.replaceState(null, "", url.toString());
}

function parseDateParam(raw: string | null): Date | undefined {
    if (!raw) {
        return undefined;
    }
    const parsed = parse(raw, DATE_URL_FORMAT, new Date());
    return isValid(parsed) ? parsed : undefined;
}

export function parseDateRangeFromURL(): DateRange | null {
    if (typeof window === "undefined") {
        return null;
    }
    const params = new URLSearchParams(window.location.search);
    const from = parseDateParam(params.get(START_DATE_URL_PARAM));
    const to = parseDateParam(params.get(END_DATE_URL_PARAM));
    if (!from && !to) {
        return null;
    }
    return { from, to };
}

export function writeDateRangeToURL(dateRange: DateRange | undefined) {
    if (typeof window === "undefined") {
        return;
    }
    const url = new URL(window.location.href);
    if (dateRange?.from) {
        url.searchParams.set(
            START_DATE_URL_PARAM,
            format(dateRange.from, DATE_URL_FORMAT),
        );
    } else {
        url.searchParams.delete(START_DATE_URL_PARAM);
    }
    if (dateRange?.to) {
        url.searchParams.set(
            END_DATE_URL_PARAM,
            format(dateRange.to, DATE_URL_FORMAT),
        );
    } else {
        url.searchParams.delete(END_DATE_URL_PARAM);
    }
    window.history.replaceState(null, "", url.toString());
}

export function getAssetsByIDsQuery(assetIDs: number[]) {
    return {
        query: {
            sort: {
                field: AssetColumns.name,
                direction: SortDirections.asc,
            },
            pagination: {
                page: 1,
                perPage: Math.max(assetIDs.length, 1),
            },
            filter: {
                operator: FilterOperators.Or,
                filters: assetIDs.map((id) => ({
                    field: AssetColumns.id,
                    operator: FilterOperators.Equals,
                    value: id,
                })),
            },
            // TODO - add fields to this query to trim the response size
            // fields: ["id", "name", "slug", "ticker", "icon_url", "color_hex"],
        },
    };
}

export function getNetFieldForStartDate(startDate: Date): NetFlowField {
    const daysAgo = (Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysAgo <= 7) return "chg_7d_amt";
    if (daysAgo <= 30) return "chg_30d_amt";
    return "chg_90d_amt";
}

export function getAssetNetValue(asset: Asset, field: NetFlowField): number {
    return (
        (asset.adjusted_daily_mints_dollar?.[field] ?? 0) -
        (asset.adjusted_daily_burns_dollar?.[field] ?? 0)
    );
}

export function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = React.useState(value);
    React.useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(id);
    }, [value, delayMs]);
    return debounced;
}
